/** "Who they are" — the lead page's left column. */
import React, { useEffect, useRef, useState } from 'react';
import { Home, Lightbulb, ShieldCheck, Tag } from 'lucide-react';
import { Lead, Understanding } from '../../types';
import { leadApi } from '../../services/api';
import { WhatsAppIcon, CallIcon } from '../BrandIcons';
import { FoldCard, errText } from './Sheet';
import { at, discoveryFields, nriLine, orderedTags, stanceChip, understandingRows } from './leadModel';

const ADVISOR_ROLES: [string, string][] = [['presales', 'Pre-sales'], ['sitevisit', 'Site visit'], ['closure', 'Closure']];

const initials = (name?: string) =>
  (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '—';

export const IdentityCard: React.FC<{
  lead: Lead;
  onCall: () => void;
  onChat: () => void;
  onPersonalWa: () => void;
  onAdvisors: () => void;
}> = ({ lead, onCall, onChat, onPersonalWa, onAdvisors }) => {
  const nri = nriLine(lead.mobile);
  return (
    <div className="card">
      <div className="flex gap-3">
        <span className="avat w-14 h-14 text-2xl flex-none">{lead.name.charAt(0).toUpperCase()}</span>
        <div className="min-w-0 text-[13px] space-y-0.5">
          <p className="num font-semibold m-0">{lead.mobile || 'no phone'}{lead.source && <span className="text-graphite font-normal"> · {lead.source}</span>}</p>
          {nri && <p className="m-0 text-amber-ink font-semibold">🌍 {nri}</p>}
          {lead.alternate_mobile && <p className="num m-0 text-graphite">alt: {lead.alternate_mobile}</p>}
          {lead.email && <p className="m-0 text-graphite truncate">{lead.email}</p>}
          {lead.location && <p className="m-0">📍 {lead.location}</p>}
          {!!lead.net_worth_estimate && lead.net_worth_estimate > 0 && (
            <p className="num m-0">Net worth est: ₹{lead.net_worth_estimate.toLocaleString('en-IN')}</p>
          )}
          {lead.acquisition_label && (
            <p className="m-0 text-graphite text-xs" title={lead.acquisition?.src}>
              Came from <span className="text-charcoal">{lead.acquisition_label}</span> · {at(lead.created_at)}
              {lead.acquisition?.video_url && (
                <> · <a href={lead.acquisition.video_url} target="_blank" rel="noreferrer" className="text-forest underline">reel ↗</a></>
              )}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3">
        <button className="btn flex flex-col items-center gap-1 !px-1 !py-2 text-[12px]" disabled={!lead.mobile} onClick={onCall}>
          <CallIcon className="w-5 h-5" />Call
        </button>
        <button className="btn flex flex-col items-center gap-1 !px-1 !py-2 text-[12px]" disabled={!lead.conversation_id} onClick={onChat}
          title={lead.conversation_id ? 'Bhuvana WhatsApp (business line)' : 'No WhatsApp conversation yet — it starts when they write in or a template goes out'}>
          <WhatsAppIcon className="w-5 h-5" />Bhuvana WA
        </button>
        <button className="btn flex flex-col items-center gap-1 !px-1 !py-2 text-[12px]" disabled={!lead.mobile} onClick={onPersonalWa}
          title="Send from your own WhatsApp">
          <span className="relative"><WhatsAppIcon className="w-5 h-5" />
            <span className="absolute -right-1.5 -bottom-1 bg-forest text-warm-white text-[8px] font-bold rounded-full w-3 h-3 grid place-items-center">✎</span>
          </span>Your WA
        </button>
      </div>

      <div className="mt-3 pt-3 border-t border-dashed border-sketch space-y-1.5 text-[12.5px]">
        {ADVISOR_ROLES.map(([stage, lbl]) => {
          const a = lead.assignments?.find((x) => x.stage === stage);
          return (
            <div key={stage} className="flex items-center gap-2">
              <span className="w-[70px] text-graphite">{lbl}</span>
              {a ? (
                <>
                  <span className="w-6 h-6 rounded-full bg-gold/20 text-amber-ink grid place-items-center text-[10px] font-bold">{initials(a.user_name)}</span>
                  <span className="truncate">{a.user_name || a.user_email}</span>
                </>
              ) : <span className="text-graphite">—</span>}
            </div>
          );
        })}
        <button className="text-forest font-semibold text-xs underline" onClick={onAdvisors}>Change advisors</button>
      </div>
    </div>
  );
};

export const DiscoveryCard: React.FC<{ lead: Lead }> = ({ lead }) => {
  const fields = discoveryFields(lead);
  const knownCount = fields.filter((f) => f.value).length;
  return (
    <FoldCard title="Discovery" icon={<Home className="w-4 h-4" strokeWidth={1.75} />} count={`${knownCount} / 8 known`}
      defaultOpen={knownCount > 0}>
      {knownCount === 0 ? (
        <p className="text-graphite text-sm italic m-0">Nothing captured yet.</p>
      ) : (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
          {fields.map((f) => (
            <div key={f.label} className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.06em] text-graphite">{f.label}</div>
              <div className={`text-[13px] ${f.value ? 'font-medium' : 'text-graphite italic'}`}>{f.value || 'not yet'}</div>
            </div>
          ))}
        </div>
      )}
    </FoldCard>
  );
};

export const UnderstandingCard: React.FC<{ understanding: Understanding | null }> = ({ understanding }) => {
  const rows = understandingRows(understanding);
  if (!rows.length) return null;
  return (
    <FoldCard title="What we know" icon={<Lightbulb className="w-4 h-4" strokeWidth={1.75} />}
      count={`${rows.length} topic${rows.length === 1 ? '' : 's'}`} defaultOpen={rows.length <= 3}>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.key}>
            <div className="flex items-center gap-1.5 flex-wrap">
              <b className="text-[13px]">{r.title}</b>
              {r.channel === 'call' && <CallIcon className="w-3.5 h-3.5" />}
              {r.channel === 'chat' && <WhatsAppIcon className="w-3.5 h-3.5" />}
              {r.stance && <span className={`chip ${stanceChip(r.stance)}`}>{r.stance.replace(/_/g, ' ')}</span>}
            </div>
            <p className="text-[12.5px] text-charcoal m-0 mt-0.5 leading-snug">{r.detail}</p>
          </div>
        ))}
      </div>
    </FoldCard>
  );
};

const Kv: React.FC<{ k: string; v?: React.ReactNode }> = ({ k, v }) =>
  v ? <div className="kv"><span>{k}</span><span className="text-right">{v}</span></div> : null;

/** Seed-matched public records. `openSignal` bumps when the header badge is
 *  clicked, so the badge and this card are one thing. */
export const PublicRecordsCard: React.FC<{ lead: Lead; openSignal: number }> = ({ lead, openSignal }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [key, setKey] = useState(0);
  useEffect(() => {
    if (!openSignal) return;
    setKey(openSignal);
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [openSignal]);

  const p = lead.enriched_profile;
  if (!p) return null;
  const e = p.enrichment || {};
  const hni = (e.hni_indicator || '').toLowerCase();
  const name = [e.title, e.first_name, e.last_name].filter(Boolean).join(' ');
  const vehicles = p.vehicles?.length ? p.vehicles
    : p.make ? [{ make: p.make, model: p.model, cc: p.vehicle_cc, vehicle_number: null }] : [];
  return (
    <div ref={ref}>
      <FoldCard key={key} title="Public records" icon={<ShieldCheck className="w-4 h-4" strokeWidth={1.75} />}
        count={hni ? <span className={`chip ${hni === 'high' ? 'chip-gold' : hni === 'medium' ? 'chip-forest' : 'chip-mute'}`}>HNI · {hni}</span> : undefined}
        defaultOpen={key > 0}>
        <Kv k="Name" v={name} />
        <Kv k="Gender" v={e.gender} />
        <Kv k="Community" v={[e.community_religion, e.community_language].filter(Boolean).join(' · ')} />
        <Kv k="Caste" v={e.caste} />
        <Kv k="Industry" v={e.industry} />
        <Kv k="Locality" v={e.locality} />
        <Kv k="Address" v={p.address} />
        <Kv k="City · PIN" v={[p.city, p.pin_code].filter(Boolean).join(' · ')} />
        <Kv k="State" v={p.state_code || e.state_code} />
        {vehicles.map((v, i) => (
          <Kv key={i} k={i === 0 ? 'Vehicles' : ''} v={<>
            {[v.make, v.model].filter(Boolean).join(' ')}
            <span className="block text-graphite text-xs num">{[v.cc && `${v.cc}cc`, v.vehicle_number].filter(Boolean).join(' · ')}</span>
          </>} />
        ))}
      </FoldCard>
    </div>
  );
};

export const TagsCard: React.FC<{ lead: Lead; onChanged: () => void }> = ({ lead, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const tags = orderedTags(lead.tags);

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key); setError(null);
    try { await fn(); onChanged(); } catch (e) { setError(errText(e)); }
    setBusy(null);
  };
  const add = () => {
    const slug = draft.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (!slug) return;
    run('add', async () => {
      await leadApi.mergeTags(lead.id, { add: [{ tag: `manual:${slug}` }] });
      setDraft(''); setAdding(false);
    });
  };

  return (
    <div className="card">
      <h3 className="card-h"><Tag className="w-4 h-4" strokeWidth={1.75} />Tags</h3>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t) => (
          <span key={t.tag} className={`chip ${t.dim === 'manual' ? 'chip-gold' : 'chip-forest'}`} title={t.tag}>
            {t.value}
            <button type="button" aria-label={`Remove ${t.value}`} disabled={!!busy}
              className="opacity-50 hover:opacity-100 ml-0.5"
              onClick={() => run(t.tag, () => leadApi.mergeTags(lead.id, { remove: [t.tag] }))}>
              {busy === t.tag ? '…' : '✕'}
            </button>
          </span>
        ))}
        {!adding && (
          <button type="button" className="chip chip-mute" onClick={() => setAdding(true)}>＋ {tags.length ? '' : 'Tag'}</button>
        )}
      </div>
      {adding && (
        <div className="flex gap-2 mt-2">
          <input id="tag-new" className="inp flex-1" autoFocus placeholder="e.g. weekend buyer" value={draft}
            onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
          <button className="btn btn-sm btn-pri" disabled={!draft.trim() || busy === 'add'} onClick={add}>Add</button>
          <button className="btn btn-sm" onClick={() => { setAdding(false); setDraft(''); }}>✕</button>
        </div>
      )}
      {error && <p className="text-brick text-xs mt-2 m-0">{error}</p>}
    </div>
  );
};
